/**
 * Доска одного проекта.
 *
 * Перенос карточки применяется сразу, до ответа сервера: доска — инструмент
 * для планёрки, и карточка, которая едет в колонку через полсекунды, читается
 * как «не сработало». Расплата за это — честный откат: снимок порядка снимается
 * до переноса и возвращается на место, если сервер отказал.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { getProject, type Project } from '@/features/projects/api'

import {
  emptyFilters,
  getBoard,
  hasAnyFilter,
  listColumnIssues,
  moveIssue,
  type BoardColumn,
  type BoardFilters,
  type BoardIssue,
} from './api'

/** Сколько держится подсветка перенесённой карточки; столько же длится вспышка */
const LANDED_MS = 1000

/**
 * Шаг раскрытия длинной колонки. Кратен странице догрузки (100): при других
 * значениях последняя страница уехала бы со своим размером, а сервер считает
 * отступ от размера страницы — и добор перечитал бы уже показанное.
 */
const COLUMN_STEP = 100

export const useBoardStore = defineStore('board', () => {
  const columns = ref<BoardColumn[]>([])
  const isLoading = ref(false)
  const error = ref<unknown>(null)
  /** Проект, чья доска сейчас в сторе; смена проекта чистит колонки */
  const projectId = ref('')
  const project = ref<Project | null>(null)
  const filters = ref<BoardFilters>(emptyFilters())

  /**
   * Раскрытые колонки: идентификатор → сколько карточек в ней просили.
   *
   * Живёт в сторе, а не в компоненте колонки, потому что доска перечитывается
   * каждые полминуты и заменяет колонки целиком. Без этой памяти раскрытая
   * колонка схлопывалась бы обратно к странице сама собой — на глазах у того,
   * кто её только что раскрыл.
   */
  const expanded = ref<Record<string, number>>({})

  /** Колонка, которую сейчас догружают: её кнопка ждёт и не жмётся повторно */
  const loadingColumnId = ref('')

  /** Пока карточку тащат, доска не перерисовывается из-под руки */
  const isDragging = ref(false)
  /** Незавершённые переносы: их ответ новее любого начатого раньше рефетча */
  const pendingMoves = ref(0)

  /**
   * Задача, которую только что перенесли. Карточка отвечает на это вспышкой:
   * после ответа сервера доска перечитывается целиком, и без метки карточка
   * растворяется среди соседей ровно в тот момент, когда её ищут глазами.
   */
  const landedId = ref('')
  let landedTimer: ReturnType<typeof setTimeout> | undefined

  const issueTotal = computed(() =>
    columns.value.reduce((total, column) => total + column.issues.length + column.hidden, 0),
  )

  const isEmpty = computed(() => columns.value.length > 0 && issueTotal.value === 0)

  /**
   * Пустая доска и доска, из которой всё вычеркнул отбор, — разные экраны:
   * первому нужна кнопка «создать», второму — «снимите фильтр».
   */
  const isFiltered = computed(() => hasAnyFilter(filters.value))

  /**
   * `silent` — для интервального обновления: скелетоны на каждом тике вместо
   * доски выглядят как поломка, а не как свежие данные.
   */
  const load = async (nextProjectId: string, { silent = false } = {}) => {
    if (nextProjectId !== projectId.value) {
      columns.value = []
      expanded.value = {}
      projectId.value = nextProjectId
    }

    if (!silent) isLoading.value = true
    error.value = null

    try {
      columns.value = await getBoard(nextProjectId, filters.value)
      await restoreExpanded()
    } catch (failure) {
      // Тихое обновление не должно стирать доску под руками: показываем
      // ошибку, только когда показывать больше нечего.
      if (!silent || !columns.value.length) error.value = failure
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Возвращает раскрытым колонкам их размер после того, как доска приехала
   * заново. Отказ догрузки здесь не роняет доску: колонка останется короткой,
   * а доска — на экране; иначе одна долгая колонка гасила бы весь экран на
   * фоновом обновлении.
   */
  const restoreExpanded = async () => {
    const wanted = Object.entries(expanded.value).filter(([id]) =>
      columns.value.some((column) => column.id === id),
    )

    await Promise.all(
      wanted.map(async ([columnId, size]) => {
        try {
          await fillColumn(columnId, size)
        } catch {
          delete expanded.value[columnId]
        }
      }),
    )
  }

  /** Заменяет карточки колонки выдачей на `size` штук вместе с её счётчиком */
  const fillColumn = async (columnId: string, size: number) => {
    const { issues, total } = await listColumnIssues(columnId, filters.value, size)
    const column = columns.value.find((entry) => entry.id === columnId)

    if (!column) return

    column.issues = issues
    column.hidden = Math.max(0, total - issues.length)
    expanded.value[columnId] = size
  }

  /**
   * «Показать ещё» — следующий шаг карточек в длинной колонке. Считаем от того,
   * что на экране: колонка могла приехать и страницей доски, и уже раскрытой.
   */
  const loadMoreColumn = async (columnId: string) => {
    const column = columns.value.find((entry) => entry.id === columnId)

    if (!column || !column.hidden || loadingColumnId.value) return

    loadingColumnId.value = columnId

    // Следующий размер округляется до целого шага: доска отдаёт полсотни, а
    // страница догрузки — сотня, и «полсотни плюс шаг» просило бы полторы
    // страницы. Лишнюю половину сервер всё равно отдал бы целой.
    const next = Math.floor(column.issues.length / COLUMN_STEP) * COLUMN_STEP + COLUMN_STEP

    try {
      await fillColumn(columnId, next)
    } finally {
      loadingColumnId.value = ''
    }
  }

  /**
   * Вход на доску: в адресе ключ проекта (`/p/DEV/board`), а доска запрашивается
   * по идентификатору. Отказ на этом шаге — это «нет такого проекта» или «не
   * для вас», и разбирается он экраном: у них разные ответы человеку.
   */
  const open = async (projectKey: string) => {
    if (project.value?.key === projectKey && columns.value.length) {
      await refresh()

      return
    }

    isLoading.value = true
    error.value = null
    project.value = null
    columns.value = []
    // Отбор принадлежит доске: с чужого проекта на новый он не переезжает.
    filters.value = emptyFilters()
    // Раскрытые колонки — тоже: идентификаторы чужие, а размер догрузки
    // достался бы новому проекту без спроса.
    expanded.value = {}

    try {
      const found = await getProject(projectKey)

      project.value = found
      projectId.value = found.id
      columns.value = await getBoard(found.id, filters.value)
    } catch (failure) {
      error.value = failure
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Доска в фоне устаревает: соседний человек переносит карточку, и без
   * обновления два экрана расходятся до перезагрузки. Во время перетаскивания
   * и пока перенос не подтверждён — не трогаем: ответ мог уйти раньше нашего.
   * Забытая во вкладках доска не опрашивает сервис вовсе: смотреть на неё
   * некому, а запросы идут каждые полминуты.
   */
  const refresh = async () => {
    if (isDragging.value || pendingMoves.value > 0 || !projectId.value) return
    if (typeof document !== 'undefined' && document.hidden) return

    await load(projectId.value, { silent: true })
  }

  /**
   * Смена отбора перезапрашивает доску, а не отсеивает карточки на месте:
   * в длинной колонке приезжает только страница, и отбор на клиенте показывал
   * бы «две задачи Михаила» там, где их десять. Скелетоны при этом не мигают —
   * их показывают, только когда колонок ещё нет вовсе.
   */
  const applyFilters = async (next: BoardFilters) => {
    filters.value = next

    if (projectId.value) await load(projectId.value)
  }

  /**
   * Порядок карточек на момент захвата — то, к чему возвращаемся при отказе
   * сервера. Снимок снимается до переноса, а не перед отправкой: к моменту
   * отправки vuedraggable карточку уже переставил, и «откат» вернул бы ровно
   * то, что нужно отменить.
   */
  let dragSnapshot: Map<string, BoardIssue[]> | null = null

  const beginDrag = () => {
    isDragging.value = true
    dragSnapshot = new Map(columns.value.map((column) => [column.id, [...column.issues]]))
  }

  const endDrag = () => {
    isDragging.value = false
  }

  /**
   * Карточку в списках уже переставил vuedraggable — здесь только отправка и
   * возврат на место при отказе. `before` пусто, когда карточка легла в конец
   * колонки.
   */
  const move = async (issueId: string, columnId: string, before?: string) => {
    const saved = dragSnapshot

    pendingMoves.value += 1

    try {
      await moveIssue(issueId, columnId, before)

      landedId.value = issueId
      clearTimeout(landedTimer)
      landedTimer = setTimeout(() => {
        landedId.value = ''
      }, LANDED_MS)
    } catch (failure) {
      if (saved) {
        for (const column of columns.value) {
          const issues = saved.get(column.id)

          if (issues) column.issues = issues
        }
      }

      throw failure
    } finally {
      pendingMoves.value -= 1
    }
  }

  return {
    columns,
    isLoading,
    error,
    project,
    projectId,
    isDragging,
    landedId,
    issueTotal,
    isEmpty,
    filters,
    isFiltered,
    loadingColumnId,
    applyFilters,
    open,
    load,
    refresh,
    loadMoreColumn,
    beginDrag,
    endDrag,
    move,
  }
})
